import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { DocumentModel } from '@data-layer/models/documents';
import { CustomValidator } from '@helpers/custom-validator';
import { UntypedFormControl } from '@angular/forms';
import { environment } from '@env/environment';
import { ALLOWED_FILE_EXTENSIONS, ALLOWED_FILE_MIMES } from '@constants/constants';

@Component({
  selector: 'document-upload-ocr',
  templateUrl: './document-upload-ocr.component.html',
  styleUrls: ['./document-upload-ocr.component.scss'],
})
export class DocumentUploadOcrComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() public id: string = 'file-input-ocr';
  @Input() public fileName: string;
  @Input() public existingFile: File;
  @Input() public validators: any[] = [
    CustomValidator.fileExtensions(ALLOWED_FILE_EXTENSIONS),
    CustomValidator.maxFileSize(environment.files.default_file_size_limit),
  ];
  @Input() public allowedFileMimes: typeof ALLOWED_FILE_MIMES = ALLOWED_FILE_MIMES;
  @Input() public isLoading: boolean;
  @Input() public isSmallIcon: boolean;
  @Input() public isList: boolean;
  @Input() public isLast: boolean;
  @Input() public text: string;
  @Input() public hint: string;
  @Output() public upload: EventEmitter<File> = new EventEmitter<File>();
  @Output() public remove: EventEmitter<DocumentModel> = new EventEmitter<DocumentModel>();

  @ViewChild('inputElement', { static: false }) public inputElement: ElementRef;

  public fileControl: UntypedFormControl;
  public errors: { [name: string]: string } = {
    maxFileSize: 'FORM_ERROR.MAX_FILE_SIZE',
    onlyImage: 'FORM_ERROR.FILE_EXTENSION_NOT_ALLOWED',
  };
  public isHoverFile: boolean = false;

  private isUploadingDoc: boolean;

  constructor(private cdr: ChangeDetectorRef) {}

  public ngOnInit(): void {
    this.fileControl = new UntypedFormControl(null, this.validators);
  }

  public ngAfterViewInit(): void {
    if (!!this.existingFile) {
      this.uploadFile(this.existingFile);
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!changes?.existingFile?.firstChange) {
      this.uploadFile(this.existingFile);
    }
  }

  public handleFiles(event: Event): void {
    const target: HTMLInputElement = event.target as HTMLInputElement;
    const files: FileList = target.files;
    if (files?.length) {
      this.isUploadingDoc = true;
      this.uploadFile(files[0]);
    }
  }

  public removeFile(): void {
    this.fileName = null;
    this.remove.emit();
  }

  public onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer.effectAllowed === 'copyLink') {
      this.isHoverFile = true;
    }
  }

  public onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer.files && event.dataTransfer.files.length) {
      this.isUploadingDoc = true;
      this.uploadFile(event.dataTransfer.files[0]);
      this.isHoverFile = false;
    }
  }

  public onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer.effectAllowed === 'copyLink') {
      this.isHoverFile = false;
    }
  }

  public clickInput(): void {
    this.clearFile();
  }

  public uploadFile(file: File): void {
    if (file) {
      const reader: FileReader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = (event: ProgressEvent<FileReader>) => {
        this.fileName = file.name;
        this.clearFile();
        this.fileControl.patchValue(file);
        this.fileControl.markAsTouched();

        this.inputElement.nativeElement.value = null;
        if (this.fileControl.valid) {
          this.upload.emit(file);
        }
        this.isUploadingDoc = false;
        this.cdr.detectChanges();
      };
    }
  }

  private clearFile(): void {
    this.fileControl.patchValue(null);
    this.fileControl.markAsUntouched();
  }
}
